'use client'
import React, { useCallback, useMemo, useEffect, useState  } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser as abiFilersContactIcon,
  faBriefcase as abiFiler1Icon,
  faList as abiFiler2Icon
} from '@fortawesome/pro-regular-svg-icons';
import RenderFormWithRHF from 'src/components/era-package/forms/RenderFormWithRHF';
import { abiFilersContact, abiFiler1, abiFiler2, optionsMap } from 'src/views/abi/fields/user-settings';
import { customTest } from '../schema/userSettings';
import AbiUserSettingsApiUrls from "../../../apiUrls/AbiUserSettingsApiUrls";
import {
  Box,
  LinearProgress
} from '@mui/material';
import SnackBar from 'src/components/era-package/era/SnackBar';

const UserSettings = () => {
  const [initialData, setInitialData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSaveSuccessSnackBar, setShowSaveSuccessSnackBar] = useState(false);
  const [showErrorSnackBar, setShowErrorSnackBar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Set up form sections
  const formSections = useMemo(
    () => [
      {
        title: "ABI Filer's Information",
        icon: <FontAwesomeIcon icon={abiFilersContactIcon} />,
        fields: abiFilersContact,
      },
      {
        title: 'ABI Filer',
        icon: <FontAwesomeIcon icon={abiFiler1Icon} />,
        fields: abiFiler1,
      },
      {
        title: 'ABI Entry Number',
        icon: <FontAwesomeIcon icon={abiFiler2Icon} />,
        fields: abiFiler2,
      },
    ],
    [],
  );

  // Set up form data
  const allFields = useMemo(
    () => formSections.flatMap((section) => section.fields),
    [formSections],
  );
  
  const isAllFieldsNull = (data) => {
    if (!data || typeof data !== 'object') return true;

    return Object.values(data).every(value => value === null);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(AbiUserSettingsApiUrls.GET, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
          },
        });
  
        const json = await response.json();
        setIsLoading(false);
  
        if (!response.ok) {
          setErrorMessage(json.message || 'Failed to fetch user settings.');
          setShowErrorSnackBar(true);
        } else {
		  const data = json.data;
		  if (!isAllFieldsNull(data)) {
            const updatedData = {
              ...data,
              entryNumberSet: data.entryNumberSet.toString(),
            };
			setInitialData(updatedData);
		  }
        }
      } catch (error) {
        setIsLoading(false);
        setErrorMessage('Unable to connect to the server. Please try again later.');
        setShowErrorSnackBar(true);
        console.error('Fetch error:', error);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (data, e) => {
	  setErrorMessage('');
	  setShowErrorSnackBar(false);

    const updatedData = {
      ...data,
      entryNumberSet: parseInt(data.entryNumberSet, 10),
    };

    const formData = new FormData();
    
    // Append all fields except declarantSignature if empty
    Object.keys(updatedData).forEach((key) => {
      if (key === 'declarantSignature') {
        const file = updatedData[key];
        // Only append if file exists and is a File object
        if (file && file instanceof File) {
          formData.append(key, file);
        }
      } else {
        formData.append(key, updatedData[key]);
      }
    });
    
    const response = await fetch(AbiUserSettingsApiUrls.SAVE, {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
      body: formData
    });

    const json = await response.json();

    if (!response.ok) {
      setErrorMessage(json.message);
      setShowErrorSnackBar(true);
    } else {
      setShowSaveSuccessSnackBar(true);

      const updatedFieldData = json.data;
      console.log(updatedFieldData);
      if (updatedFieldData) {
        const updatedSubmittedData = {
          ...updatedFieldData,
          entryNumberSet: updatedFieldData.entryNumberSet.toString(),
        };
        setInitialData((prevData) => ({
          ...prevData,
          ...updatedSubmittedData, // merge updated fields
        }));
      }
	}
  };

  // Custom validation function for React Hook Form
  const customValidation = useCallback((formData) => {
    const newErrors = {};

    // Validate ranges
    const start = parseInt(formData.entryNumberRangeStart);
    const end = parseInt(formData.entryNumberRangeEnd);

    if (start && end && start > end) {
      newErrors.entryNumberRangeStart =
        'The Start of the Entry Number Range cannot be greater than its End';
      newErrors.entryNumberRangeEnd =
        'The End of the Entry Number Range cannot be lesser than its Start ';
    }

    return Object.keys(newErrors).length === 0;
  }, []);

  return (
	<>
    {isLoading && (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    )}

    <SnackBar
      title="Saved successfully"
      type="success"
      open={showSaveSuccessSnackBar}
      onClose={() => setShowSaveSuccessSnackBar(false)}
      duration={2000}
    >
      User settings saved successfully
    </SnackBar>

    <SnackBar
      title="Error"
      type="error"
      open={showErrorSnackBar}
      onClose={() => setShowErrorSnackBar(false)}
      //duration={6000}
    >
      {errorMessage}
    </SnackBar>

	{!isLoading && (
	<RenderFormWithRHF
		title="General"
		formSections={formSections}
		allFields={allFields}
		optionsMap={optionsMap}
		schema={customTest}
		customValidation={customValidation}
		onSubmit={handleSubmit}
		initialData={initialData}
        hideSave={true}
	/>
	)}
	
    </>
  );
};

export default UserSettings;
